import React, { useMemo, useState } from "react";
import { auth } from "./firebase";
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
} from "firebase/auth";

const Auth = ({ onLogin }) => {
    const [mode, setMode] = useState("login");
    const [loginForm, setLoginForm] = useState({ email: "", password: "", remember: true });
    const [registerForm, setRegisterForm] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const isLogin = mode === "login";

    const features = useMemo(
        () => [
            { title: "Lançamentos rápidos", desc: "Registre entradas e saídas em segundos." },
            { title: "Visão clara do mês", desc: "Relatórios e gráficos para decidir melhor." },
            { title: "Alertas inteligentes", desc: "Lembretes de contas a vencer." },
            { title: "Sincronização", desc: "Dados atualizados em tempo real." },
        ],
        []
    );

    const friendlyAuthError = (err) => {
        const code = err?.code || "";
        if (code === "auth/invalid-email") return "Email inválido.";
        if (code === "auth/user-not-found") return "Conta não encontrada.";
        if (code === "auth/wrong-password") return "Senha incorreta.";
        if (code === "auth/invalid-credential") return "Email ou senha incorretos.";
        if (code === "auth/too-many-requests") return "Muitas tentativas. Tente novamente em alguns minutos.";
        if (code === "auth/email-already-in-use") return "Este email já está cadastrado!";
        if (code === "auth/weak-password") return "Senha fraca. Use pelo menos 6 caracteres.";
        return "Ocorreu um erro. Tente novamente.";
    };

    const handleLogin = async () => {
        if (!loginForm.email || !loginForm.password) {
            setError("Preencha email e senha.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const result = await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
            onLogin(result.user);
        } catch (err) {
            setError(friendlyAuthError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async () => {
        if (!registerForm.name || !registerForm.email || !registerForm.password || !registerForm.confirmPassword) {
            setError("Preencha todos os campos.");
            return;
        }
        if (registerForm.password !== registerForm.confirmPassword) {
            setError("As senhas não coincidem.");
            return;
        }
        if (registerForm.password.length < 6) {
            setError("A senha deve ter pelo menos 6 caracteres.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const result = await createUserWithEmailAndPassword(auth, registerForm.email, registerForm.password);
            onLogin(result.user);
        } catch (err) {
            setError(friendlyAuthError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError("");
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            onLogin(result.user);
        } catch (err) {
            if (err.code !== "auth/popup-closed-by-user") {
                setError("Erro ao entrar com Google. Tente novamente.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onEnter = (e, fn) => {
        if (e.key === "Enter") fn();
    };

    return (
        <div className="auth-root">
            <div className="blob blob-a" />
            <div className="blob blob-b" />

            <div className="auth-grid">
                {/* LEFT */}
                <aside className="auth-left">
                    <div className="brand">
                        <img src={process.env.PUBLIC_URL + "/logo.png"} alt="FinTrack" className="brand-logo" />
                        <span className="brand-name">FinTrack</span>
                    </div>

                    <h1 className="hero-title">
                        Tenha clareza do seu dinheiro
                        <br />
                        <span className="hero-gradient">Sem planilhas</span>
                    </h1>


                    <p className="hero-sub">
                        Organize entradas, saídas e metas em um só lugar. Veja o mês com mais controle e menos ansiedade.
                    </p>

                    <div className="feature-list">
                        {features.map((f) => (
                            <div className="feature" key={f.title}>
                                <div className="dot" />
                                <div>
                                    <div className="feature-title">{f.title}</div>
                                    <div className="feature-desc">{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="trust">
                        <div className="trust-badge">
                            <span className="lock" aria-hidden="true">🔒</span>
                            Seus dados são protegidos e criptografados.
                        </div>
                    </div>

                    <div className="mockup" aria-hidden="true">
                        <div className="mockup-top">
                            <div className="pill" />
                            <div className="pill sm" />
                            <div className="pill sm" />
                        </div>
                        <div className="mockup-body">
                            <div className="card-mini">
                                <div className="mini-title" />
                                <div className="mini-value" />
                            </div>
                            <div className="card-mini">
                                <div className="mini-title" />
                                <div className="mini-value" />
                            </div>
                            <div className="chart">
                                <div className="bar b1" />
                                <div className="bar b2" />
                                <div className="bar b3" />
                                <div className="bar b4" />
                                <div className="bar b5" />
                            </div>
                        </div>
                    </div>

                    <div className="copyright">© 2026 FinTrack. Todos os direitos reservados.</div>
                </aside>

                {/* RIGHT */}
                <main className="auth-right">
                    <div className="right-wrap">
                        <div className="brand mobile">
                            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="FinTrack" className="brand-logo" />
                            <span className="brand-name">FinTrack</span>
                        </div>

                        <section className="card">
                            <header className="card-header">
                                <h2 className="card-title">{isLogin ? "Acesse seu controle financeiro" : "Crie sua conta"}</h2>
                                <p className="card-sub">
                                    {isLogin ? "Entre para continuar." : "Leva menos de 1 minuto para começar."}
                                </p>
                            </header>

                            {error && (
                                <div className="alert" role="alert" aria-live="polite">
                                    {error}
                                </div>
                            )}

                            {/* Botão Google — aparece em ambos os modos */}
                            <button
                                type="button"
                                className="btn secondary"
                                onClick={handleGoogleLogin}
                                disabled={loading}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                {loading ? "Aguarde..." : "Continuar com Google"}
                            </button>

                            <div className="divider"><span>ou use seu email</span></div>

                            {isLogin ? (
                                <div className="form">
                                    <div className="field">
                                        <label className="label" htmlFor="login-email">Email</label>
                                        <input
                                            id="login-email"
                                            className="input"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={loginForm.email}
                                            autoComplete="email"
                                            onChange={(e) => setLoginForm((s) => ({ ...s, email: e.target.value }))}
                                            onKeyDown={(e) => onEnter(e, handleLogin)}
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="field">
                                        <label className="label" htmlFor="login-password">Senha</label>
                                        <div className="input-wrap">
                                            <input
                                                id="login-password"
                                                className="input"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="••••••••"
                                                value={loginForm.password}
                                                autoComplete="current-password"
                                                onChange={(e) => setLoginForm((s) => ({ ...s, password: e.target.value }))}
                                                onKeyDown={(e) => onEnter(e, handleLogin)}
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="ghost"
                                                onClick={() => setShowPassword((v) => !v)}
                                                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                            >
                                                {showPassword ? "Ocultar" : "Ver"}
                                            </button>
                                        </div>
                                        <div className="row-between">
                                            <label className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={loginForm.remember}
                                                    onChange={(e) => setLoginForm((s) => ({ ...s, remember: e.target.checked }))}
                                                    disabled={loading}
                                                />
                                                <span>Lembrar de mim</span>
                                            </label>
                                            <button
                                                type="button"
                                                className="link"
                                                onClick={() => setError("Em breve: redefinição de senha por email.")}
                                                disabled={loading}
                                            >
                                                Esqueci minha senha
                                            </button>
                                        </div>
                                    </div>

                                    <button type="button" className="btn" onClick={handleLogin} disabled={loading}>
                                        {loading ? "Entrando..." : "Entrar com email"}
                                    </button>

                                    <p className="switch">
                                        Não tem conta?{" "}
                                        <button
                                            type="button"
                                            className="link strong"
                                            onClick={() => { setMode("register"); setError(""); }}
                                            disabled={loading}
                                        >
                                            Criar Conta
                                        </button>
                                    </p>
                                </div>
                            ) : (
                                <div className="form">
                                    <div className="field">
                                        <label className="label" htmlFor="reg-name">Nome completo</label>
                                        <input
                                            id="reg-name"
                                            className="input"
                                            type="text"
                                            placeholder="Seu nome"
                                            value={registerForm.name}
                                            autoComplete="name"
                                            onChange={(e) => setRegisterForm((s) => ({ ...s, name: e.target.value }))}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="field">
                                        <label className="label" htmlFor="reg-email">Email</label>
                                        <input
                                            id="reg-email"
                                            className="input"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={registerForm.email}
                                            autoComplete="email"
                                            onChange={(e) => setRegisterForm((s) => ({ ...s, email: e.target.value }))}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="field">
                                        <label className="label" htmlFor="reg-pass">Senha</label>
                                        <input
                                            id="reg-pass"
                                            className="input"
                                            type="password"
                                            placeholder="Mínimo 6 caracteres"
                                            value={registerForm.password}
                                            autoComplete="new-password"
                                            onChange={(e) => setRegisterForm((s) => ({ ...s, password: e.target.value }))}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="field">
                                        <label className="label" htmlFor="reg-confirm">Confirmar senha</label>
                                        <input
                                            id="reg-confirm"
                                            className="input"
                                            type="password"
                                            placeholder="Repita a senha"
                                            value={registerForm.confirmPassword}
                                            autoComplete="new-password"
                                            onChange={(e) => setRegisterForm((s) => ({ ...s, confirmPassword: e.target.value }))}
                                            onKeyDown={(e) => onEnter(e, handleRegister)}
                                            disabled={loading}
                                        />
                                    </div>
                                    <button type="button" className="btn" onClick={handleRegister} disabled={loading}>
                                        {loading ? "Criando conta..." : "Criar conta com email"}
                                    </button>
                                    <p className="switch">
                                        Já tem conta?{" "}
                                        <button
                                            type="button"
                                            className="link strong"
                                            onClick={() => { setMode("login"); setError(""); }}
                                            disabled={loading}
                                        >
                                            Entrar
                                        </button>
                                    </p>
                                </div>
                            )}
                        </section>

                        <p className="microcopy">
                            Ao continuar, você concorda com os termos e política de privacidade.
                        </p>
                    </div>
                </main>
            </div>

            <style>{`
        :root{
          --bg: #05080b;
          --card: rgba(255,255,255,0.05);
          --border: rgba(255,255,255,0.10);
          --muted: rgba(255,255,255,0.55);
          --muted2: rgba(255,255,255,0.40);
          --text: rgba(255,255,255,0.92);
          --green: #22c55e;
          --green2:#10b981;
        }
        .auth-root{
          min-height: 100vh;
          background: radial-gradient(circle at 20% 30%, rgba(34,197,94,.16), transparent 42%),
                      radial-gradient(circle at 85% 70%, rgba(16,185,129,.12), transparent 40%),
                      var(--bg);
          position: relative; overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .blob{ position:absolute; pointer-events:none; filter: blur(2px); opacity:.18; }
        .blob-a{ top:-180px; left:-180px; width:540px; height:540px; background: radial-gradient(circle, var(--green), transparent 65%); }
        .blob-b{ bottom:-160px; right:-140px; width:520px; height:520px; background: radial-gradient(circle, var(--green2), transparent 65%); }
        .auth-grid{ min-height:100vh; display:grid; grid-template-columns: 1fr; }
        .auth-left{ display:none; padding: 64px 72px; border-right: 1px solid rgba(255,255,255,0.06); position:relative; z-index:2; }
        .auth-right{ display:flex; align-items:center; justify-content:center; padding: 40px 24px; position:relative; z-index:2; }
        .right-wrap{ width:100%; max-width: 480px; }
        .brand{ display:flex; align-items:center; gap:10px; margin-bottom: 42px; }
        .brand.mobile{ justify-content:center; }
        .brand-logo{ width:32px; height:32px; }
        .brand-name{ color: var(--text); font-weight:800; font-size: 20px; letter-spacing: .2px; }
        .hero-title{ color: var(--text); font-size: 46px; line-height: 1.08; margin: 0 0 18px 0; font-weight: 900; letter-spacing: -0.8px; max-width: 560px; }
        .hero-gradient{ background: linear-gradient(135deg, #4ade80, #34d399); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .hero-sub{ color: var(--muted); font-size: 15px; line-height: 1.75; max-width: 520px; margin: 0 0 28px 0; }
        .feature-list{ display:flex; flex-direction:column; gap:14px; margin-top: 6px; }
        .feature{ display:flex; gap:12px; align-items:flex-start; }
        .dot{ width:8px; height:8px; border-radius:999px; background: var(--green); margin-top:7px; box-shadow: 0 0 0 4px rgba(34,197,94,.12); }
        .feature-title{ color: rgba(255,255,255,0.88); font-size: 14px; font-weight: 700; }
        .feature-desc{ color: var(--muted2); font-size: 13px; margin-top: 2px; }
        .trust{ margin-top: 22px; }
        .trust-badge{ display:inline-flex; gap:10px; align-items:center; padding: 10px 12px; border-radius: 12px; border: 1px solid rgba(34,197,94,.22); background: rgba(34,197,94,.08); color: rgba(255,255,255,0.82); font-size: 13px; }
        .mockup{ margin-top: 28px; width: 560px; max-width: 100%; border-radius: 18px; border: 1px solid rgba(255,255,255,0.08); background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03)); box-shadow: 0 35px 80px rgba(0,0,0,0.45); overflow: hidden; }
        .mockup-top{ height: 44px; display:flex; align-items:center; gap:10px; padding: 0 14px; border-bottom: 1px solid rgba(255,255,255,0.06); background: rgba(0,0,0,0.14); }
        .pill{ width: 96px; height: 10px; border-radius: 999px; background: rgba(255,255,255,0.10); }
        .pill.sm{ width: 46px; opacity:.9; }
        .mockup-body{ padding: 16px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .card-mini{ border-radius: 14px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); padding: 14px; }
        .mini-title{ width: 60%; height: 10px; background: rgba(255,255,255,0.10); border-radius:999px; }
        .mini-value{ width: 40%; height: 14px; margin-top: 12px; background: rgba(34,197,94,0.22); border-radius:999px; }
        .chart{ grid-column: 1 / -1; height: 120px; border-radius: 14px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.03); padding: 14px; display:flex; align-items:flex-end; gap: 10px; }
        .bar{ width: 18%; border-radius: 10px; background: linear-gradient(180deg, rgba(34,197,94,.75), rgba(16,185,129,.25)); }
        .b1{ height: 55%; } .b2{ height: 78%; } .b3{ height: 42%; } .b4{ height: 90%; } .b5{ height: 62%; }
        .copyright{ margin-top: 22px; color: rgba(255,255,255,0.22); font-size: 12px; }
        .card{ border-radius: 20px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05); backdrop-filter: blur(10px); box-shadow: 0 40px 90px rgba(0,0,0,0.55); padding: 34px 30px; }
        .card-header{ margin-bottom: 18px; }
        .card-title{ color: var(--text); font-size: 20px; margin: 0 0 6px 0; font-weight: 800; letter-spacing: -0.2px; }
        .card-sub{ color: var(--muted); font-size: 13px; margin:0; }
        .alert{ margin-top: 14px; margin-bottom: 14px; padding: 11px 12px; border-radius: 12px; border: 1px solid rgba(239,68,68,.28); background: rgba(239,68,68,.10); color: rgba(248,113,113,.95); font-size: 13px; }
        .form{ display:flex; flex-direction:column; gap: 14px; }
        .field{ display:flex; flex-direction:column; gap: 7px; }
        .label{ color: rgba(255,255,255,0.45); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }
        .input{ width:100%; box-sizing:border-box; padding: 13px 14px; border-radius: 12px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10); color: rgba(255,255,255,0.92); outline: none; font-size: 14px; transition: box-shadow .18s, border-color .18s, transform .12s; }
        .input::placeholder{ color: rgba(255,255,255,0.28); }
        .input:focus{ border-color: rgba(34,197,94,0.7); box-shadow: 0 0 0 4px rgba(34,197,94,0.18); }
        .input:disabled{ opacity: .65; cursor:not-allowed; }
        .input-wrap{ position:relative; display:flex; align-items:center; }
        .ghost{ position:absolute; right: 10px; padding: 8px 10px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.10); background: rgba(0,0,0,0.12); color: rgba(255,255,255,0.60); cursor:pointer; font-size: 12px; font-weight: 700; }
        .ghost:hover{ border-color: rgba(255,255,255,0.18); color: rgba(255,255,255,0.78); }
        .row-between{ display:flex; align-items:center; justify-content:space-between; margin-top: 10px; }
        .checkbox{ display:flex; align-items:center; gap:8px; color: rgba(255,255,255,0.55); font-size: 13px; }
        .checkbox input{ accent-color: var(--green); }
        .btn{ margin-top: 4px; height: 52px; width: 100%; border: none; border-radius: 14px; font-size: 15px; font-weight: 800; color: rgba(255,255,255,0.96); cursor: pointer; background: linear-gradient(135deg, var(--green), var(--green2)); box-shadow: 0 14px 35px rgba(34,197,94,0.22); transition: transform .12s, box-shadow .2s, opacity .2s; display:flex; align-items:center; justify-content:center; }
        .btn:hover{ transform: translateY(-1px); box-shadow: 0 20px 45px rgba(34,197,94,0.28); }
        .btn:disabled{ opacity:.65; cursor:not-allowed; transform:none; }
        .btn.secondary{ background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); box-shadow: none; margin-top: 0; }
        .btn.secondary:hover{ transform: translateY(-1px); border-color: rgba(255,255,255,0.20); }
        .divider{ display:flex; align-items:center; justify-content:center; margin: 14px 0; position:relative; height: 22px; }
        .divider::before{ content:""; position:absolute; left:0; right:0; height:1px; background: rgba(255,255,255,0.10); }
        .divider span{ position:relative; padding: 0 10px; color: rgba(255,255,255,0.38); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }
        .link{ background:none; border:none; padding:0; cursor:pointer; color: rgba(74,222,128,0.95); font-weight: 700; font-size: 13px; }
        .link:hover{ text-decoration: underline; }
        .switch{ text-align:center; color: rgba(255,255,255,0.55); font-size: 13px; margin: 6px 0 0 0; }
        .microcopy{ margin-top: 14px; text-align:center; color: rgba(255,255,255,0.30); font-size: 12px; }
        @media (min-width: 1024px){
          .auth-grid{ grid-template-columns: 1.2fr 0.8fr; }
          .auth-left{ display:block; }
          .brand.mobile{ display:none; }
          .right-wrap{ max-width: 520px; }
        }
      `}</style>
        </div>
    );
};

export default Auth;
